import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import {
  Plus, Pencil, Trash2, GripVertical, Copy, ChevronDown, ChevronUp,
  HelpCircle, ListChecks, Star, ToggleLeft, Hash, Save, X, Loader2,
  CheckCircle2, AlertCircle, FileQuestion, Building2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const QUESTION_TYPES = [
  { value: 'text', label: 'Text Answer', icon: HelpCircle },
  { value: 'multiple_choice', label: 'Multiple Choice', icon: ListChecks },
  { value: 'rating', label: 'Rating (1-10)', icon: Star },
  { value: 'yes_no', label: 'Yes / No', icon: ToggleLeft },
  { value: 'scale', label: 'Scale (1-5)', icon: Hash }
];

const QuestionBankManager = ({ getAuthToken, companies = [] }) => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBank, setSelectedBank] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Companies and positions state
  const [crmCompanies, setCrmCompanies] = useState([]);
  const [companyPositions, setCompanyPositions] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingPositions, setLoadingPositions] = useState(false);
  const positionsRequest = useRef(0);
  const [manualPosition, setManualPosition] = useState(false);
  
  // Bank form state
  const [bankForm, setBankForm] = useState({
    name: '',
    description: '',
    company_id: '',
    company_name: '',
    position_id: '',
    job_id: '',
    position_name: '',
    is_active: true,
    questions: []
  });
  
  // Question dialog state
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    question_type: 'text',
    options: [],
    expected_answer: '',
    weightage: 5,
    category: '',
    hint: '',
    is_required: true,
    is_active: true
  });

  // Fetch question banks
  const fetchBanks = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/question-banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanks(res.data);
    } catch (error) {
      console.error('Failed to fetch question banks:', error);
      toast.error('Failed to load question banks');
    } finally {
      setLoading(false);
    }
  }, [getAuthToken]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  // Fetch companies from CRM
  const fetchCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCrmCompanies(res.data || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      // Fallback to prop if API fails
      if (companies.length > 0) {
        setCrmCompanies(companies);
      }
    } finally {
      setLoadingCompanies(false);
    }
  }, [getAuthToken, companies]);

  // Fetch positions for selected company
  const fetchPositionsForCompany = useCallback(async (companyId, companyName) => {
    const requestId = ++positionsRequest.current;
    setCompanyPositions([]);
    if (!companyId && !companyName) {
      setLoadingPositions(false);
      return;
    }
    
    setLoadingPositions(true);
    try {
      const token = getAuthToken();
      
      // Try the dedicated endpoint first (handles case-insensitive matching on server)
      let jobs = [];
      try {
        const res = await axios.get(`${API}/job-updates/by-company/${encodeURIComponent((companyName || '').trim())}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { company_id: companyId || undefined }
        });
        jobs = res.data || [];
      } catch (err) {
        // Fallback to getting all jobs and filtering client-side
        console.log('Falling back to client-side filtering');
        const res = await axios.get(`${API}/job-updates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        jobs = res.data || [];
      }

      // Do not offer another company's similarly named jobs (e.g. Amazon Pay).
      const companyNameLower = (companyName || '').trim().toLowerCase();
      const positions = jobs.filter(job =>
        (companyId && job.company_id === companyId) ||
        (companyNameLower && (job.company_name || '').trim().toLowerCase() === companyNameLower)
      ).map(job => ({
        id: job.id,
        title: job.position_name || job.title || 'Untitled position',
        company_name: job.company_name
      }));
      
      if (requestId === positionsRequest.current) setCompanyPositions(positions);
      console.log(`Found ${positions.length} positions for company: ${companyName}`);
    } catch (error) {
      console.error('Failed to fetch positions:', error);
      if (requestId === positionsRequest.current) setCompanyPositions([]);
    } finally {
      if (requestId === positionsRequest.current) setLoadingPositions(false);
    }
  }, [getAuthToken]);

  // Load companies when editing mode is activated
  useEffect(() => {
    if (isEditing && crmCompanies.length === 0) {
      fetchCompanies();
    }
  }, [isEditing, crmCompanies.length, fetchCompanies]);

  // Reload on each editor opening, even when the company stayed the same.
  useEffect(() => {
    if (isEditing) fetchPositionsForCompany(bankForm.company_id, bankForm.company_name);
    else positionsRequest.current += 1;
  }, [isEditing, bankForm.company_id, bankForm.company_name, fetchPositionsForCompany]);

  // Create new bank
  const handleCreateBank = () => {
    setManualPosition(false);
    setBankForm({
      name: '',
      description: '',
      company_id: '',
      company_name: '',
      position_id: '',
      job_id: '',
      position_name: '',
      is_active: true,
      questions: []
    });
    setCompanyPositions([]);
    setSelectedBank(null);
    setIsEditing(true);
  };

  // Edit existing bank
  const handleEditBank = (bank) => {
    setManualPosition(!bank.job_id && !bank.position_id && !!bank.position_name?.trim());
    setBankForm({
      name: bank.name || '',
      description: bank.description || '',
      company_id: bank.company_id || '',
      company_name: bank.company_name || '',
      position_id: bank.position_id || '',
      job_id: bank.job_id || bank.position_id || '',
      position_name: bank.position_name || '',
      is_active: bank.is_active,
      questions: bank.questions || []
    });
    setSelectedBank(bank);
    setIsEditing(true);
  };

  // Save bank
  const handleSaveBank = async () => {
    if (isSaving) return;
    if (!bankForm.company_name.trim()) {
      toast.error('Please select a company');
      return;
    }
    
    // Auto-generate name from company + position if not set
    let bankName = bankForm.name;
    if (!bankName.trim()) {
      bankName = bankForm.position_name 
        ? `${bankForm.company_name} - ${bankForm.position_name}` 
        : `${bankForm.company_name} Screening`;
    }
    
    const dataToSave = {
      ...bankForm,
      name: bankName,
      job_id: bankForm.job_id || null,
      position_id: bankForm.position_id || bankForm.job_id || null
    };
    
    setIsSaving(true);
    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      if (selectedBank) {
        // Update existing
        await axios.patch(`${API}/question-banks/${selectedBank.id}`, dataToSave, config);
        toast.success('Question bank updated');
      } else {
        // Create new
        await axios.post(`${API}/question-banks`, dataToSave, config);
        toast.success('Question bank created');
      }
      
      setIsEditing(false);
      setSelectedBank(null);
      setCompanyPositions([]);
      fetchBanks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save question bank');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete bank
  const handleDeleteBank = async (bankId) => {
    if (!window.confirm('Are you sure you want to delete this question bank?')) return;
    
    try {
      const token = getAuthToken();
      await axios.delete(`${API}/question-banks/${bankId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Question bank deleted');
      fetchBanks();
    } catch (error) {
      toast.error('Failed to delete question bank');
    }
  };

  // Duplicate bank
  const handleDuplicateBank = (bank) => {
    setManualPosition(!bank.job_id && !bank.position_id && !!bank.position_name?.trim());
    setBankForm({
      name: `${bank.name} (Copy)`,
      description: bank.description || '',
      company_id: bank.company_id || '',
      company_name: bank.company_name || '',
      position_id: bank.position_id || '',
      job_id: bank.job_id || bank.position_id || '',
      position_name: bank.position_name || '',
      is_active: true,
      questions: bank.questions?.map(q => ({ ...q, id: undefined })) || []
    });
    setSelectedBank(null);
    setIsEditing(true);
  };

  // Add/Edit question
  const openQuestionDialog = (question = null) => {
    if (question) {
      setQuestionForm({
        ...question,
        options: question.options || []
      });
      setEditingQuestion(question);
    } else {
      setQuestionForm({
        question_text: '',
        question_type: 'text',
        options: [],
        expected_answer: '',
        weightage: 5,
        category: '',
        hint: '',
        is_required: true,
        is_active: true
      });
      setEditingQuestion(null);
    }
    setQuestionDialogOpen(true);
  };

  const saveQuestion = () => {
    if (!questionForm.question_text.trim()) {
      toast.error('Please enter the question text');
      return;
    }
    
    const newQuestion = {
      ...questionForm,
      id: editingQuestion?.id || `q_${Date.now()}`,
      order: editingQuestion ? editingQuestion.order : bankForm.questions.length
    };
    
    if (editingQuestion) {
      setBankForm(prev => ({
        ...prev,
        questions: prev.questions.map(q => q.id === editingQuestion.id ? newQuestion : q)
      }));
    } else {
      setBankForm(prev => ({
        ...prev,
        questions: [...prev.questions, newQuestion]
      }));
    }
    
    setQuestionDialogOpen(false);
    toast.success(editingQuestion ? 'Question updated' : 'Question added');
  };

  const deleteQuestion = (questionId) => {
    setBankForm(prev => ({
      ...prev,
      questions: prev.questions.filter(q => q.id !== questionId)
    }));
    toast.success('Question removed');
  };

  // Move question up/down
  const moveQuestion = (index, direction) => {
    const newQuestions = [...bankForm.questions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newQuestions.length) return;
    
    [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
    newQuestions.forEach((q, i) => q.order = i);
    
    setBankForm(prev => ({ ...prev, questions: newQuestions }));
  };

  // Add option for multiple choice
  const addOption = () => {
    setQuestionForm(prev => ({
      ...prev,
      options: [...prev.options, { id: `opt_${Date.now()}`, text: '', is_correct: false, points: 0 }]
    }));
  };

  const updateOption = (index, field, value) => {
    setQuestionForm(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? { ...opt, [field]: value } : opt)
    }));
  };

  const removeOption = (index) => {
    setQuestionForm(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
      </div>
    );
  }

  // Editing view
  if (isEditing) {
    return (
      <div className="space-y-6 min-w-0" data-testid="question-bank-editor">
        <div className="flex flex-wrap gap-3 items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-800">
              {selectedBank ? 'Edit Question Bank' : 'Create Question Bank'}
            </h2>
            <p className="text-sm text-slate-500 mt-1">Define questions for telephonic screening</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="question-bank-cancel" variant="outline" onClick={() => setIsEditing(false)}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button data-testid="question-bank-save" onClick={handleSaveBank} disabled={isSaving || loadingPositions} className="bg-violet-600 hover:bg-violet-700">
              {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Bank
            </Button>
          </div>
        </div>

        {/* Bank Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Screening Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="question-bank-company">Company Name *</Label>
                <Select
                  value={bankForm.company_id || ''}
                  onValueChange={(value) => {
                    const company = crmCompanies.find(c => c.id === value);
                    setManualPosition(false);
                    setCompanyPositions([]);
                    setBankForm(prev => ({
                      ...prev,
                      company_id: value,
                      company_name: company?.name || company?.company_name || '',
                      position_id: '',
                      job_id: '',
                      position_name: '',
                      name: '' // Reset name to auto-generate
                    }));
                  }}
                >
                  <SelectTrigger id="question-bank-company" data-testid="question-bank-company-select" className={`[&>span]:truncate ${loadingCompanies ? 'opacity-50' : ''}`}>
                    <SelectValue placeholder={loadingCompanies ? "Loading companies..." : "Select a company"} />
                  </SelectTrigger>
                  <SelectContent data-testid="question-bank-company-options" className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]">
                    {crmCompanies.map(company => (
                      <SelectItem key={company.id} value={company.id} data-testid={`question-bank-company-${company.id}`} className="whitespace-normal [overflow-wrap:anywhere] [&>span:last-child]:min-w-0">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400" />
                          {company.name || company.company_name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {crmCompanies.length === 0 && !loadingCompanies && (
                  <p data-testid="question-bank-no-companies" className="text-xs text-amber-600">No companies found. Add companies in the Companies tab first.</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="question-bank-position">Job / Position</Label>
                <Select value={bankForm.job_id || bankForm.position_id || (manualPosition ? '_manual_entry_' : '_company_wide_')}
                  disabled={!bankForm.company_name || loadingPositions}
                  onValueChange={value => {
                    const position = companyPositions.find(p => p.id === value);
                    setManualPosition(value === '_manual_entry_');
                    setBankForm(prev => ({ ...prev, job_id: position?.id || '', position_id: position?.id || '',
                      position_name: position?.title || '', name: '' }));
                  }}>
                  <SelectTrigger id="question-bank-position" data-testid="question-bank-position-select" className="[&>span]:truncate">
                    <SelectValue placeholder={loadingPositions ? 'Loading jobs…' : 'Select job / position'} />
                  </SelectTrigger>
                  <SelectContent data-testid="question-bank-position-options" className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-2rem)]">
                    <SelectItem value="_company_wide_" data-testid="question-bank-company-wide-option">All positions (company-wide)</SelectItem>
                    {companyPositions.map(position => <SelectItem key={position.id} value={position.id} data-testid={`question-bank-position-${position.id}`} className="whitespace-normal [overflow-wrap:anywhere] [&>span:last-child]:min-w-0">
                      {position.title} · {position.id.slice(-8)}
                    </SelectItem>)}
                    {(bankForm.job_id || bankForm.position_id) && !companyPositions.some(p => p.id === (bankForm.job_id || bankForm.position_id)) &&
                      <SelectItem value={bankForm.job_id || bankForm.position_id} data-testid="question-bank-linked-position" className="whitespace-normal [overflow-wrap:anywhere] [&>span:last-child]:min-w-0">{bankForm.position_name || 'Linked position'} (saved)</SelectItem>}
                    <SelectItem value="_manual_entry_" data-testid="question-bank-manual-option">Enter position manually</SelectItem>
                  </SelectContent>
                </Select>
                {loadingPositions && <p data-testid="question-bank-positions-loading" role="status" className="text-xs text-slate-500">Loading jobs…</p>}
                {manualPosition && <Input data-testid="question-bank-manual-position" value={bankForm.position_name}
                  onChange={e => setBankForm(prev => ({ ...prev, position_name: e.target.value, job_id: '', position_id: '', name: '' }))}
                  placeholder="Position name" />}
              </div>
            </div>
            
            {/* Auto-generated name preview */}
            {bankForm.company_name && (
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <Label className="text-xs text-slate-500">Screening Bank Name (Auto-generated)</Label>
                <p data-testid="question-bank-name-preview" className="font-medium text-slate-700 [overflow-wrap:anywhere]">
                  {bankForm.position_name 
                    ? `${bankForm.company_name} - ${bankForm.position_name}` 
                    : `${bankForm.company_name} Screening`}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                data-testid="question-bank-description"
                value={bankForm.description}
                onChange={(e) => setBankForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the purpose of this screening bank..."
                rows={2}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                data-testid="question-bank-active"
                checked={bankForm.is_active}
                onCheckedChange={(checked) => setBankForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label>Active (available for screening)</Label>
            </div>
          </CardContent>
        </Card>

        {/* Questions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Questions ({bankForm.questions.length})</CardTitle>
            <Button onClick={() => openQuestionDialog()} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Question
            </Button>
          </CardHeader>
          <CardContent>
            {bankForm.questions.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileQuestion className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No questions added yet</p>
                <p className="text-sm">Click "Add Question" to start building your screening</p>
              </div>
            ) : (
              <div className="space-y-3">
                {bankForm.questions
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((question, index) => (
                    <div
                      key={question.id}
                      className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-violet-300 transition-colors"
                    >
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => moveQuestion(index, 'up')}
                          disabled={index === 0}
                          className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"
                        >
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <GripVertical className="h-4 w-4 text-slate-400" />
                        <button
                          onClick={() => moveQuestion(index, 'down')}
                          disabled={index === bankForm.questions.length - 1}
                          className="p-1 hover:bg-slate-200 rounded disabled:opacity-30"
                        >
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-medium text-slate-800">{index + 1}. {question.question_text}</p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <Badge variant="secondary" className="text-xs">
                                {QUESTION_TYPES.find(t => t.value === question.question_type)?.label || question.question_type}
                              </Badge>
                              <Badge variant="outline" className="text-xs">
                                Weight: {question.weightage}
                              </Badge>
                              {question.is_required && (
                                <Badge className="text-xs bg-red-100 text-red-700">Required</Badge>
                              )}
                              {question.category && (
                                <Badge variant="outline" className="text-xs">{question.category}</Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openQuestionDialog(question)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteQuestion(question.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {question.question_type === 'multiple_choice' && question.options?.length > 0 && (
                          <div className="mt-2 pl-4 border-l-2 border-slate-200">
                            {question.options.map((opt, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                                <span className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-xs">
                                  {String.fromCharCode(65 + i)}
                                </span>
                                {opt.text}
                                {opt.is_correct && (
                                  <CheckCircle2 className="h-3 w-3 text-green-500" />
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {question.hint && (
                          <p className="mt-2 text-xs text-slate-500 italic">Hint: {question.hint}</p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Question Dialog */}
        <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
          <DialogContent data-testid="question-bank-question-dialog" className="w-[calc(100%-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add Question'}</DialogTitle>
              <DialogDescription>Screening question and scoring criteria</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Question Text *</Label>
                <Textarea
                  data-testid="question-bank-question-text"
                  value={questionForm.question_text}
                  onChange={(e) => setQuestionForm(prev => ({ ...prev, question_text: e.target.value }))}
                  placeholder="Enter your question..."
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Question Type</Label>
                  <Select
                    value={questionForm.question_type}
                    onValueChange={(v) => setQuestionForm(prev => ({ ...prev, question_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {QUESTION_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          <span className="flex items-center gap-2">
                            <type.icon className="h-4 w-4" />
                            {type.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Weightage (1-10)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={questionForm.weightage}
                    onChange={(e) => setQuestionForm(prev => ({ ...prev, weightage: parseInt(e.target.value) || 5 }))}
                  />
                </div>
              </div>
              
              {questionForm.question_type === 'multiple_choice' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Answer Options</Label>
                    <Button variant="outline" size="sm" onClick={addOption}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Option
                    </Button>
                  </div>
                  {questionForm.options.map((opt, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium">
                        {String.fromCharCode(65 + index)}
                      </span>
                      <Input
                        value={opt.text}
                        onChange={(e) => updateOption(index, 'text', e.target.value)}
                        placeholder={`Option ${String.fromCharCode(65 + index)}`}
                        className="flex-1"
                      />
                      <div className="flex items-center gap-1">
                        <input
                          type="checkbox"
                          checked={opt.is_correct}
                          onChange={(e) => updateOption(index, 'is_correct', e.target.checked)}
                          className="h-4 w-4"
                          title="Correct answer"
                        />
                        <span className="text-xs text-slate-500">Correct</span>
                      </div>
                      <Input
                        type="number"
                        value={opt.points}
                        onChange={(e) => updateOption(index, 'points', parseInt(e.target.value) || 0)}
                        className="w-16"
                        placeholder="Pts"
                        title="Points for this option"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeOption(index)}
                        className="text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {questionForm.question_type === 'text' && (
                <div className="space-y-2">
                  <Label>Expected Answer (for reference)</Label>
                  <Textarea
                    value={questionForm.expected_answer}
                    onChange={(e) => setQuestionForm(prev => ({ ...prev, expected_answer: e.target.value }))}
                    placeholder="What is the ideal answer? (helps recruiters evaluate)"
                    rows={2}
                  />
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category (Optional)</Label>
                  <Input
                    value={questionForm.category}
                    onChange={(e) => setQuestionForm(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="e.g., Communication, Technical"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hint for Recruiter</Label>
                  <Input
                    value={questionForm.hint}
                    onChange={(e) => setQuestionForm(prev => ({ ...prev, hint: e.target.value }))}
                    placeholder="Tips for evaluating the answer"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={questionForm.is_required}
                    onCheckedChange={(checked) => setQuestionForm(prev => ({ ...prev, is_required: checked }))}
                  />
                  <Label>Required</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={questionForm.is_active}
                    onCheckedChange={(checked) => setQuestionForm(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label>Active</Label>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button data-testid="question-bank-question-cancel" variant="outline" onClick={() => setQuestionDialogOpen(false)}>
                Cancel
              </Button>
              <Button data-testid="question-bank-question-save" onClick={saveQuestion} className="bg-violet-600 hover:bg-violet-700">
                {editingQuestion ? 'Update Question' : 'Add Question'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List view
  return (
    <div className="space-y-6 min-w-0" data-testid="question-bank-manager">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Question Banks</h2>
          <p className="text-sm text-slate-500 mt-1">Manage screening questions for telephonic interviews</p>
        </div>
        <Button data-testid="question-bank-create" onClick={handleCreateBank} className="bg-violet-600 hover:bg-violet-700">
          <Plus className="h-4 w-4 mr-2" />
          Create Question Bank
        </Button>
      </div>

      {banks.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileQuestion className="h-16 w-16 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700">No Question Banks Yet</h3>
            <p className="text-sm text-slate-500 mt-1 mb-4">Create your first question bank for telephonic screening</p>
            <Button data-testid="question-bank-create-empty" onClick={handleCreateBank}>
              <Plus className="h-4 w-4 mr-2" />
              Create Question Bank
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {banks.map(bank => (
            <Card key={bank.id} data-testid={`question-bank-${bank.id}`} className={`hover:shadow-md transition-shadow ${!bank.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-5">
                <div className="flex flex-wrap gap-3 items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 data-testid={`question-bank-name-${bank.id}`} className="text-lg font-semibold text-slate-800 [overflow-wrap:anywhere]">{bank.name}</h3>
                      {bank.is_active ? (
                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                      ) : (
                        <Badge variant="secondary">Inactive</Badge>
                      )}
                    </div>
                    {bank.description && (
                      <p className="text-sm text-slate-500 mt-1">{bank.description}</p>
                    )}
                    <div className="flex flex-wrap gap-3 mt-3">
                      {bank.company_name && (
                        <div data-testid={`question-bank-company-name-${bank.id}`} className="flex items-center gap-1 text-sm text-violet-600 bg-violet-50 px-2 py-1 rounded [overflow-wrap:anywhere]">
                          <Building2 className="h-4 w-4" />
                          {bank.company_name}
                        </div>
                      )}
                      {bank.position_name && (
                        <div data-testid={`question-bank-position-name-${bank.id}`} className="flex flex-wrap items-center gap-1 text-sm text-slate-600 [overflow-wrap:anywhere]">
                          <span className="font-medium">Position:</span>
                          {bank.position_name}
                        </div>
                      )}
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <FileQuestion className="h-4 w-4" />
                        {bank.questions?.length || 0} questions
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDuplicateBank(bank)}
                      data-testid={`question-bank-duplicate-${bank.id}`}
                      title="Duplicate"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditBank(bank)}
                      data-testid={`question-bank-edit-${bank.id}`}
                    >
                      <Pencil className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteBank(bank.id)}
                      data-testid={`question-bank-delete-${bank.id}`}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuestionBankManager;
